import { getAudioData, AudioData, visualizeAudio } from "@remotion/media-utils";
import { useMemo, useState, useEffect } from "react";
import {
	useCurrentFrame,
	useVideoConfig,
	continueRender,
	delayRender,
} from "remotion";
import { AudioTrack } from "../../types/schema";
import { CROSSFADE_DURATION_FRAMES, VISUALIZER_LAYOUT } from "../constants";

interface UseVisualizerDataProps {
	audioTracks: AudioTrack[];
	schedule: Array<{
		startFrame: number;
		endFrame: number;
		duration: number;
		index: number;
	}>;
	barsToDisplay: number;
	isTest?: boolean;
}

export const useVisualizerData = ({
	audioTracks,
	schedule,
	barsToDisplay,
	isTest,
}: UseVisualizerDataProps) => {
	const frame = useCurrentFrame();
	const { fps } = useVideoConfig();

	// Dynamic audio data loading - supports unlimited tracks
	const [audioDatas, setAudioDatas] = useState<(AudioData | null)[]>([]);
	const [handle] = useState(() =>
		delayRender("Loading audio data for visualizer"),
	);

	useEffect(() => {
		let cancelled = false;

		const loadAllAudioData = async () => {
			try {
				const results = await Promise.all(
					audioTracks.map(async (track) => {
						if (!track.url) return null;
						try {
							return await getAudioData(track.url);
						} catch (err) {
							console.warn(`Failed to load audio data for ${track.url}:`, err);
							return null;
						}
					}),
				);

				if (!cancelled) {
					setAudioDatas(results);
					continueRender(handle);
				}
			} catch (err) {
				console.error("Failed to load audio data:", err);
				if (!cancelled) {
					continueRender(handle);
				}
			}
		};

		loadAllAudioData();

		return () => {
			cancelled = true;
			continueRender(handle);
		};
	}, [audioTracks, handle]);

	const { SAMPLES_TO_FETCH, MIN_BIN_INDEX, MAX_BIN_INDEX } = VISUALIZER_LAYOUT;

	// Mix audio samples based on crossfades
	const mixedSamples = useMemo(() => {
		const mixed = new Float32Array(SAMPLES_TO_FETCH).fill(0);

		if (isTest) {
			// Generate irregular test data that mimics real audio spectrum
			// Real music is chaotic, not smooth mathematical curves
			for (let i = 0; i < SAMPLES_TO_FETCH; i++) {
				const t = frame * 0.08;
				const x = i / SAMPLES_TO_FETCH;

				// Multiple layered random patterns for irregularity
				const r1 = Math.sin(t * 3.7 + i * 0.13) * Math.random();
				const r2 = Math.cos(t * 2.3 - i * 0.07) * Math.random();
				const r3 = Math.sin(t * 5.1 + i * 0.19) * Math.random();

				// Occasional sharp peaks (like snare hits, cymbal crashes)
				const spike = Math.random() > 0.95 ? Math.random() * 0.5 : 0;

				// Bass-heavy behavior (lower frequencies = higher amplitude)
				const bassBoost = Math.exp(-x * 6);

				// Combine all chaotic elements
				const chaotic = (r1 * 0.2 + r2 * 0.15 + r3 * 0.1 + spike) * bassBoost;

				// Add some base rhythm that varies
				const rhythm = Math.sin(t * 4 + Math.random() * 0.5) * 0.15 * bassBoost;

				// Final value with realistic range
				mixed[i] = Math.max(
					0,
					Math.min(0.5, Math.abs(chaotic + rhythm) * 0.35),
				);
			}
			return mixed;
		}

		for (let index = 0; index < schedule.length; index++) {
			const track = schedule[index];
			const { startFrame, endFrame, duration } = track;

			// Skip if track is not playing at current frame
			if (frame < startFrame || frame >= endFrame) continue;

			const audioData = audioDatas[index];

			// If audio is missing, we keep hasSignal false unless another track provides it
			if (!audioData) continue;

			const trackFrame = frame - startFrame;
			let volume = 1;

			// Calculate crossfade volume
			const isFirst = index === 0;
			const isLast = index === schedule.length - 1;

			if (!isFirst && trackFrame < CROSSFADE_DURATION_FRAMES) {
				// Fade in
				volume = trackFrame / CROSSFADE_DURATION_FRAMES;
			} else if (!isLast && trackFrame > duration - CROSSFADE_DURATION_FRAMES) {
				// Fade out
				volume =
					1 -
					(trackFrame - (duration - CROSSFADE_DURATION_FRAMES)) /
					CROSSFADE_DURATION_FRAMES;
			}

			if (volume <= 0.01) continue;

			const samples = visualizeAudio({
				fps,
				frame: trackFrame,
				audioData,
				numberOfSamples: SAMPLES_TO_FETCH,
			});

			for (let i = 0; i < SAMPLES_TO_FETCH; i++) {
				const val = samples[i] * volume;
				mixed[i] += val;
			}
		}

		return mixed;
	}, [
		frame,
		audioDatas,
		schedule,
		fps,
		isTest,
		SAMPLES_TO_FETCH,
		CROSSFADE_DURATION_FRAMES,
	]);

	// Process samples into bars
	const smoothedBars = useMemo(() => {
		// Validasi barsToDisplay
		if (!barsToDisplay || barsToDisplay <= 0) {
			return new Float32Array(0);
		}

		const bars = new Float32Array(barsToDisplay);

		for (let i = 0; i < barsToDisplay; i++) {
			const t = i / barsToDisplay;
			// Logarithmic scaling for frequency mapping
			const logStart = t * t * t;
			const logEnd = ((i + 1) / barsToDisplay) ** 3;

			const startSampleIndex = Math.floor(
				MIN_BIN_INDEX + logStart * (MAX_BIN_INDEX - MIN_BIN_INDEX),
			);
			const endSampleIndex = Math.floor(
				MIN_BIN_INDEX + logEnd * (MAX_BIN_INDEX - MIN_BIN_INDEX),
			);

			let sum = 0;
			let count = 0;
			const actualEnd = Math.max(startSampleIndex, endSampleIndex);

			for (
				let j = startSampleIndex;
				j <= actualEnd && j < SAMPLES_TO_FETCH;
				j++
			) {
				const sample = mixedSamples[j];
				if (isFinite(sample)) {
					sum += sample;
					count++;
				}
			}

			let value = count > 0 ? sum / count : 0;

			// 1. Flat Volume Boost (2.0x) - "Spiky" Style
			// Makes everything louder/visible but keeps the sharp peaks.
			value *= 2.0;

			// 2. Subtle High Compensation
			// High notes naturally have less energy, so give them a tiny lift.
			value *= (1 + t * 0.5);

			// Clamp to 1.0
			bars[i] = isFinite(value) ? Math.min(value, 1) : 0;
		}

		// Return raw bars (no smoothing) for sharp/spiky look
		return bars;
	}, [
		mixedSamples,
		barsToDisplay,
		MIN_BIN_INDEX,
		MAX_BIN_INDEX,
		SAMPLES_TO_FETCH,
	]);

	return smoothedBars;
};
